import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
} from '@mui/material';

import {
  Search,
  FilterList,
  Download,
  Refresh,
  Clear,
  Inventory,
  Receipt,
  Warning,
  CalendarToday,
  CheckCircle,
  Pending,
  RemoveCircle,
  Verified,
  History,
  Store,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

import Sidebar from '../../components/sidebars/Sidebar_accountant';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CHART_COLORS = ['#1a237e', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#00695c', '#c2185b'];

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 450px)',
  minHeight: '400px',
  '& .MuiTable-root': {
    minWidth: 800,
    width: '100%',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transition: 'background-color 0.2s ease',
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

const MainContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.black, 0.05),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.4),
    borderRadius: '4px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.6),
    },
  },
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const AccountantInventoryDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [accountantName, setAccountantName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');

  const [inventoryItems, setInventoryItems] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [totalInventory, setTotalInventory] = useState(0);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [inventoryStats, setInventoryStats] = useState({
    totalItems: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    healthyStockItems: 0,
    topCategories: [],
    categoryDistribution: [],
    stockTurnover: 0,
    avgStockValue: 0,
    totalReceived: 0,
    totalIssued: 0,
  });

  const [requisitionStats, setRequisitionStats] = useState({
    totalRequests: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    approvalRate: 0,
    avgResponseTime: 0,
    topRequestedItems: [],
    monthlyTrends: [],
    departmentBreakdown: [],
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // ============ CALCULATE INVENTORY STATS ============
  const calculateInventoryStats = useCallback((items) => {
    const totalValue = items.reduce((sum, item) => sum + ((item.quantity_available || 0) * (item.unit_cost || 0)), 0);
    const lowStockItems = items.filter(item => item.quantity_available <= (item.lowstock_threshold || 5) && item.quantity_available > 0).length;
    const outOfStockItems = items.filter(item => item.quantity_available <= 0).length;
    const healthyStockItems = items.filter(item => item.quantity_available > (item.lowstock_threshold || 5)).length;
    const totalReceived = items.reduce((sum, item) => sum + (item.quantity_received || 0), 0);
    const totalIssued = items.reduce((sum, item) => sum + (item.quantity_issued || 0), 0);
    
    const categoryMap = new Map();
    items.forEach(item => {
      const cat = item.category || 'Uncategorized';
      categoryMap.set(cat, (categoryMap.get(cat) || 0) + 1);
    });
    
    const categoryDistribution = Array.from(categoryMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
    
    const avgStock = items.reduce((sum, item) => sum + (item.quantity_available || 0), 0) / (items.length || 1);
    const stockTurnover = avgStock > 0 ? totalIssued / avgStock : 0;
    
    setInventoryStats({
      totalItems: items.length,
      totalValue: Math.round(totalValue),
      lowStockItems,
      outOfStockItems,
      healthyStockItems,
      topCategories: [],
      categoryDistribution,
      stockTurnover: Math.round(stockTurnover * 100) / 100,
      avgStockValue: items.length > 0 ? totalValue / items.length : 0,
      totalReceived,
      totalIssued,
    });
  }, []);

  // ============ CALCULATE REQUISITION STATS ============
  const calculateRequisitionStats = useCallback((items) => {
    const pendingCount = items.filter(r => r.status === 'Pending').length;
    const approvedCount = items.filter(r => r.status === 'Approved by GM' || r.status === 'Received' || r.status === 'Issued').length;
    const rejectedCount = items.filter(r => r.status === 'Rejected').length;
    const approvalRate = items.length > 0 ? (approvedCount / items.length) * 100 : 0;
    
    const itemMap = new Map();
    items.forEach(req => {
      itemMap.set(req.item_name, (itemMap.get(req.item_name) || 0) + (parseFloat(req.quantity_requested) || 0));
    });
    const topRequestedItems = Array.from(itemMap.entries())
      .map(([name, quantity]) => ({ name, quantity: Math.round(quantity) }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
    
    const deptMap = new Map();
    items.forEach(req => {
      const dept = req.department || 'Other';
      deptMap.set(dept, (deptMap.get(dept) || 0) + 1);
    });
    const departmentBreakdown = Array.from(deptMap.entries())
      .map(([department, count]) => ({ department, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
    
    const monthlyMap = new Map();
    items.forEach(req => {
      if (req.request_date) {
        const date = new Date(req.request_date);
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
        const current = monthlyMap.get(month) || { month, pending: 0, approved: 0, rejected: 0 };
        if (req.status === 'Pending') current.pending++;
        else if (req.status === 'Approved by GM' || req.status === 'Received' || req.status === 'Issued') current.approved++;
        else if (req.status === 'Rejected') current.rejected++;
        monthlyMap.set(month, current);
      }
    });
    const monthlyTrends = Array.from(monthlyMap.values()).slice(-6);
    
    const approvedRequests = items.filter(r => r.updated_at && (r.status === 'Approved by GM' || r.status === 'Received'));
    const totalResponseDays = approvedRequests.reduce((sum, r) => {
      const requestDate = new Date(r.request_date);
      const updateDate = new Date(r.updated_at);
      const days = Math.ceil((updateDate - requestDate) / (1000 * 60 * 60 * 24));
      return sum + days;
    }, 0);
    const avgResponseTime = approvedRequests.length > 0 ? totalResponseDays / approvedRequests.length : 0;
    
    setRequisitionStats({
      totalRequests: items.length,
      pendingCount,
      approvedCount,
      rejectedCount,
      approvalRate: Math.round(approvalRate * 100) / 100,
      avgResponseTime: Math.round(avgResponseTime * 10) / 10,
      topRequestedItems,
      monthlyTrends,
      departmentBreakdown,
    });
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory/get_categories.php`);
      if (response.data.success && response.data.categories?.length > 0) {
        setCategories(response.data.categories);
      } else {
        setCategories(['Restaurant', 'Lounge', 'Arcade', 'Cosmetics', 'Salon', 'Fashion', 'Spa', 'Hall', 'Admin', 'Others']);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['Restaurant', 'Lounge', 'Arcade', 'Cosmetics', 'Salon', 'Fashion', 'Spa', 'Hall', 'Admin', 'Others']);
    }
  }, []);

  // Fetch inventory items
  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_inventory_items.php?${params}`);
      
      if (response.data.success) {
        setInventoryItems(response.data.data || []);
        setTotalInventory(response.data.total || 0);
        
        const allParams = new URLSearchParams();
        if (searchQuery) allParams.append('search', searchQuery);
        if (selectedCategory !== 'all') allParams.append('category', selectedCategory);
        allParams.append('limit', 9999);
        allParams.append('offset', 0);
        
        const allItemsResponse = await axios.get(`${API_BASE_URL}/inventory/get_inventory_items.php?${allParams}`);
        if (allItemsResponse.data.success) {
          const allItems = allItemsResponse.data.data || [];
          calculateInventoryStats(allItems);
        }
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setSnackbar({ open: true, message: 'Failed to fetch inventory', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, page, rowsPerPage, calculateInventoryStats]);

  // Fetch requisitions
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_requisitions.php?${params}`);
      
      if (response.data.success) {
        let requisitionData = response.data.data || [];
        
        if (selectedStatus !== 'all') {
          requisitionData = requisitionData.filter(r => r.status === selectedStatus);
        }
        
        if (dateRange.start) {
          requisitionData = requisitionData.filter(r => r.request_date >= dateRange.start);
        }
        
        if (dateRange.end) {
          requisitionData = requisitionData.filter(r => r.request_date <= dateRange.end);
        }
        
        setRequisitions(requisitionData);
        setTotalRequisitions(requisitionData.length);
        calculateRequisitionStats(requisitionData);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setSnackbar({ open: true, message: 'Failed to fetch requisitions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedStatus, dateRange, page, rowsPerPage, calculateRequisitionStats]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    if (userName) setAccountantName(userName);
    else if (userEmail) setAccountantName(userEmail);
    else setAccountantName('Accountant');
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      fetchInventoryItems();
    } else if (activeTab === 1) {
      fetchRequisitions();
    }
    fetchCategories();
  }, [activeTab, fetchInventoryItems, fetchRequisitions, fetchCategories]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCategory, selectedStatus, dateRange.start, dateRange.end]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setOrderBy('');
    setOrder('asc');
  };

  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setPage(0);
    handleFilterClose();
    if (activeTab === 0) {
      fetchInventoryItems();
    } else {
      fetchRequisitions();
    }
  };

  const handleRefresh = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setPage(0);
    if (activeTab === 0) {
      fetchInventoryItems();
    } else {
      fetchRequisitions();
    }
  };

  const sortData = (data) => {
    if (!orderBy || !data || data.length === 0) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_available' || orderBy === 'quantity_received' || orderBy === 'quantity_issued' || 
          orderBy === 'quantity_requested') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (orderBy === 'date_received' || orderBy === 'request_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getCurrentData = () => {
    if (activeTab === 0) return sortData(inventoryItems);
    if (activeTab === 1) return sortData(requisitions);
    return [];
  };

  const filteredData = getCurrentData();
  const totalItems = activeTab === 0 ? totalInventory : totalRequisitions;

  const handleExportToExcel = () => {
    let exportData = [];
    let fileName = '';
    
    if (activeTab === 0) {
      exportData = inventoryItems.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Current Stock': item.quantity_available,
        'Total Received': item.quantity_received,
        'Total Issued': item.quantity_issued,
        'Unit Cost (₦)': item.unit_cost || 0,
        'Total Value (₦)': ((item.quantity_available || 0) * (item.unit_cost || 0)).toFixed(2),
        'Low Stock Threshold': item.lowstock_threshold,
        'Status': item.quantity_available <= 0 ? 'Out of Stock' : 
                  (item.quantity_available <= (item.lowstock_threshold || 5) ? 'Low Stock' : 'Available'),
        'Storage Location': item.storage_location || 'N/A',
        'Date Received': item.date_received,
      }));
      fileName = 'inventory_stock_report';
    } else if (activeTab === 1) {
      exportData = requisitions.map(item => ({
        'Item Name': item.item_name,
        'Quantity Requested': item.quantity_requested,
        'Unit': item.unit_of_measurement,
        'Request Date': item.request_date,
        'Requested By': item.requested_by,
        'Department': item.department,
        'Status': item.status,
        'Notes': item.notes || 'N/A',
      }));
      fileName = 'requisitions_report';
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({ open: true, message: 'Export successful!', severity: 'success' });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₦0.00';
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 2 }).format(amount);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return new Intl.NumberFormat('en-NG').format(Math.round(num));
  };

  const getStockStatus = (item) => {
    if (item.quantity_available <= 0) return { label: 'Out of Stock', color: 'error', icon: <RemoveCircle fontSize="small" /> };
    if (item.quantity_available <= (item.lowstock_threshold || 5)) return { label: 'Low Stock', color: 'warning', icon: <Warning fontSize="small" /> };
    return { label: 'Available', color: 'success', icon: <CheckCircle fontSize="small" /> };
  };

  const getStatusColor = (status) => {
    if (status === 'Approved by GM' || status === 'Received' || status === 'Issued') return 'success';
    if (status === 'Pending') return 'warning';
    if (status === 'Rejected') return 'error';
    return 'default';
  };

  const renderInventoryTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Item Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Current Stock</TableCell>
            <TableCell align="right">Total Received</TableCell>
            <TableCell align="right">Total Issued</TableCell>
            <TableCell align="right">Unit Cost (₦)</TableCell>
            <TableCell align="right">Total Value (₦)</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell>Date Received</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={10} align="center"><CircularProgress /></TableCell></TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow><TableCell colSpan={10} align="center">No inventory items found</TableCell></TableRow>
          ) : (
            filteredData.map((item) => {
              const stockStatus = getStockStatus(item);
              const totalValue = (item.quantity_available || 0) * (item.unit_cost || 0);
              return (
                <TableRow key={item.id} hover>
                  <TableCell><Typography fontWeight={600}>{item.item_name}</Typography></TableCell>
                  <TableCell><Chip label={item.category || 'Uncategorized'} size="small" /></TableCell>
                  <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                  <TableCell align="right">{formatNumber(item.quantity_available)}</TableCell>
                  <TableCell align="right">{formatNumber(item.quantity_received)}</TableCell>
                  <TableCell align="right">{formatNumber(item.quantity_issued)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.unit_cost || 0)}</TableCell>
                  <TableCell align="right"><strong>{formatCurrency(totalValue)}</strong></TableCell>
                  <TableCell align="center">
                    <Chip label={stockStatus.label} size="small" color={stockStatus.color} />
                  </TableCell>
                  <TableCell>{item.date_received}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </StyledTableContainer>
  );

  const renderRequisitionsTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Item Name</TableCell>
            <TableCell align="right">Quantity</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Request Date</TableCell>
            <TableCell>Requested By</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
          ) : filteredData.length === 0 ? (
            <TableRow><TableCell colSpan={8} align="center">No requisitions found</TableCell></TableRow>
          ) : (
            filteredData.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell><Typography fontWeight={600}>{item.item_name}</Typography></TableCell>
                <TableCell align="right">{formatNumber(item.quantity_requested)}</TableCell>
                <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                <TableCell>{item.request_date}</TableCell>
                <TableCell>{item.requested_by}</TableCell>
                <TableCell><Chip label={item.department || 'N/A'} size="small" /></TableCell>
                <TableCell>
                  <Chip 
                    label={item.status}
                    size="small"
                    color={getStatusColor(item.status)}
                  />
                </TableCell>
                <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{item.notes || 'N/A'}</Typography></TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </StyledTableContainer>
  );

  const renderInventoryAnalytics = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 3, px: 2, pt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Total Items</Typography>
                <Typography variant="h5" fontWeight={700}>{inventoryStats.totalItems}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}><Inventory /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Total Stock Value</Typography>
                <Typography variant="h6" fontWeight={700}>{formatCurrency(inventoryStats.totalValue)}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}><Store /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Low Stock Items</Typography>
                <Typography variant="h5" fontWeight={700} color="warning.main">{inventoryStats.lowStockItems}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}><Warning /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Out of Stock</Typography>
                <Typography variant="h5" fontWeight={700} color="error.main">{inventoryStats.outOfStockItems}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}><RemoveCircle /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3, px: 2 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600} align="center">Inventory Category Distribution</Typography>
            {inventoryStats.categoryDistribution && inventoryStats.categoryDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie 
                    data={inventoryStats.categoryDistribution} 
                    cx="50%" 
                    cy="50%" 
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={130}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {inventoryStats.categoryDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value, name, props) => [`${value} items`, props.payload.name]} />
                  <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 8 }}>No data available for category distribution</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  const renderRequisitionsAnalytics = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 3, px: 2, pt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Total Requisitions</Typography>
                <Typography variant="h5" fontWeight={700}>{requisitionStats.totalRequests}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}><Receipt /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Approval Rate</Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">{requisitionStats.approvalRate}%</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}><CheckCircle /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Avg Response Time</Typography>
                <Typography variant="h5" fontWeight={700}>{requisitionStats.avgResponseTime} days</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}><CalendarToday /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Pending Requests</Typography>
                <Typography variant="h5" fontWeight={700} color="warning.main">{requisitionStats.pendingCount}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}><Pending /></Avatar>
            </Box>
          </MetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3, px: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>Monthly Requisition Trends</Typography>
            {requisitionStats.monthlyTrends && requisitionStats.monthlyTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={requisitionStats.monthlyTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Area type="monotone" dataKey="pending" stackId="1" fill="#ffc107" stroke="#ffc107" name="Pending" />
                  <Area type="monotone" dataKey="approved" stackId="1" fill="#4caf50" stroke="#4caf50" name="Approved" />
                  <Area type="monotone" dataKey="rejected" stackId="1" fill="#f44336" stroke="#f44336" name="Rejected" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 8 }}>No trend data available</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>Top Requested Items</Typography>
            {requisitionStats.topRequestedItems && requisitionStats.topRequestedItems.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={requisitionStats.topRequestedItems} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(value) => formatNumber(value)} />
                  <Bar dataKey="quantity" fill="#ed6c02" name="Quantity Requested" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 8 }}>No data available</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3, px: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>Requisition Status Breakdown</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie 
                  data={[
                    { name: 'Approved', value: requisitionStats.approvedCount },
                    { name: 'Pending', value: requisitionStats.pendingCount },
                    { name: 'Rejected', value: requisitionStats.rejectedCount },
                  ]} 
                  cx="50%" 
                  cy="50%" 
                  innerRadius={50} 
                  outerRadius={90} 
                  dataKey="value" 
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ strokeWidth: 1 }}
                >
                  <Cell fill="#4caf50" />
                  <Cell fill="#ffc107" />
                  <Cell fill="#f44336" />
                </Pie>
                <RechartsTooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>Requisitions by Department</Typography>
            {requisitionStats.departmentBreakdown && requisitionStats.departmentBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={requisitionStats.departmentBreakdown} margin={{ bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" angle={-35} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#0288d1" name="Number of Requests" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 8 }}>No data available</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  const renderCurrentTable = () => {
    if (activeTab === 0) return renderInventoryTable();
    if (activeTab === 1) return renderRequisitionsTable();
    return null;
  };

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh', overflow: 'hidden' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <MainContentWrapper>
        <Box sx={{ p: 3, ml: isMobile ? 0 : sidebarOpen ? 0 : 0, transition: 'margin 0.3s ease-in-out', maxWidth: '1600px', mx: 'auto', width: '100%', overflowX: 'hidden' }}>
          <TopNav onLogout={() => { localStorage.removeItem('authToken'); localStorage.removeItem('userProfile'); }} onNotificationClick={() => console.log('Notifications')} toggleSidebar={toggleSidebar} />
         
          <Box sx={{ mt: 3, width: '100%', overflowX: 'hidden' }}>
            <Paper elevation={0} sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', width: '100%' }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Typography variant="h5" fontWeight={700}>Inventory & Requisitions Dashboard</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Welcome, {accountantName}. Track inventory levels, requisitions, and financial metrics.
                </Typography>
              </Box>

              <Tabs value={activeTab} onChange={handleTabChange} variant={isMobile ? "scrollable" : "standard"} scrollButtons={isMobile ? "auto" : false} sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', py: 2 },
                '& .Mui-selected': { color: theme.palette.primary.main },
              }}>
                <Tab icon={<Inventory />} iconPosition="start" label={`Inventory (${totalInventory})`} />
                <Tab icon={<Receipt />} iconPosition="start" label={`Requisitions (${totalRequisitions})`} />
              </Tabs>

              {/* Analytics Sections */}
              {activeTab === 0 && inventoryStats.totalItems > 0 && renderInventoryAnalytics()}
              {activeTab === 1 && requisitionStats.totalRequests > 0 && renderRequisitionsAnalytics()}

              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder={`Search by ${activeTab === 0 ? 'item name or category' : 'item name or requester'}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (<InputAdornment position="start"><Search color="action" /></InputAdornment>),
                        endAdornment: searchQuery && (<InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><Clear fontSize="small" /></IconButton></InputAdornment>)
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                      <GlowingButton variant="outlined" startIcon={<FilterList />} onClick={handleFilterClick}>Filters</GlowingButton>
                      <GlowingButton variant="outlined" startIcon={<Download />} onClick={handleExportToExcel}>Export</GlowingButton>
                      <IconButton onClick={handleRefresh}><Refresh /></IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {((selectedCategory !== 'all' && activeTab === 0) || (selectedStatus !== 'all' && activeTab === 1) || dateRange.start || dateRange.end) && (
                <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">Active Filters:</Typography>
                  {selectedCategory !== 'all' && activeTab === 0 && (<Chip label={`Category: ${selectedCategory}`} onDelete={() => setSelectedCategory('all')} size="small" />)}
                  {selectedStatus !== 'all' && activeTab === 1 && (<Chip label={`Status: ${selectedStatus}`} onDelete={() => setSelectedStatus('all')} size="small" />)}
                  {dateRange.start && (<Chip label={`From: ${dateRange.start}`} onDelete={() => setDateRange({ ...dateRange, start: '' })} size="small" />)}
                  {dateRange.end && (<Chip label={`To: ${dateRange.end}`} onDelete={() => setDateRange({ ...dateRange, end: '' })} size="small" />)}
                  <Button size="small" onClick={clearFilters}>Clear All</Button>
                </Box>
              )}

              {renderCurrentTable()}
            </Paper>
          </Box>
        </Box>
      </MainContentWrapper>

      <Popover open={Boolean(filterAnchorEl)} anchorEl={filterAnchorEl} onClose={handleFilterClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { p: 2, width: 320 } }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Filters</Typography>
        
        {activeTab === 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select value={selectedCategory} onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(0);
            }} label="Category">
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(cat => (<MenuItem key={cat} value={cat}>{cat}</MenuItem>))}
            </Select>
          </FormControl>
        )}
        
        {activeTab === 1 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={selectedStatus} onChange={(e) => {
              setSelectedStatus(e.target.value);
              setPage(0);
            }} label="Status">
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved by GM">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        )}
        
        <TextField fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} sx={{ mb: 2 }} />
        <TextField fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} sx={{ mb: 2 }} />
        
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters} size="small">Clear All</Button>
          <Button variant="contained" onClick={handleFilterClose} size="small">Apply</Button>
        </Stack>
      </Popover>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountantInventoryDashboard;